/**
 * WebhooksPanel — manage per-user webhook endpoints.
 *
 * Creating an endpoint reveals the signing secret EXACTLY ONCE (it is only on
 * the create response; no read API returns it). The revealed secret is held in
 * local state and cleared when dismissed — it is never written to the query
 * cache. Each endpoint can be tested ("Send test") and deleted.
 */
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useState, type FormEvent, type ReactElement } from "react";
import {
  useCreateWebhook,
  useDeleteWebhook,
  useTestWebhook,
  useWebhooks,
} from "./hooks/useWebhooks";
import type { TestWebhookResponse } from "@records-monitor/shared";

export function WebhooksPanel(): ReactElement {
  const { data, isLoading, isError } = useWebhooks();
  const create = useCreateWebhook();
  const del = useDeleteWebhook();
  const test = useTestWebhook();

  const [url, setUrl] = useState("");
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestWebhookResponse | null>(null);

  const handleCreate = (event: FormEvent): void => {
    event.preventDefault();
    if (url.trim() === "") return;
    create.mutate(
      { url: url.trim() },
      {
        onSuccess: (response) => {
          // Show the secret once; never persisted anywhere readable.
          setRevealedSecret(response.secret);
          setUrl("");
        },
      },
    );
  };

  const handleTest = (id: string): void => {
    test.mutate(id, { onSuccess: (result) => setTestResult(result) });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress aria-label="Loading webhooks" />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Alert severity="error" role="alert">
        Could not load your webhook endpoints.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" component="h2" gutterBottom>
        Webhooks
      </Typography>

      <Box component="form" onSubmit={handleCreate} sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
          <TextField
            label="Endpoint URL"
            type="url"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="https://example.com/webhooks/records-monitor"
            size="small"
            sx={{ flexGrow: 1 }}
          />
          <Button type="submit" variant="contained" disabled={create.isPending}>
            Add webhook
          </Button>
        </Stack>
        {create.isError ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            Could not create the webhook. Check the URL and try again.
          </Alert>
        ) : null}
      </Box>

      {revealedSecret !== null ? (
        <Alert
          severity="warning"
          onClose={() => setRevealedSecret(null)}
          sx={{ mb: 3 }}
          role="alert"
        >
          <Typography variant="subtitle2">
            Copy your signing secret now — it is shown only once.
          </Typography>
          <Typography
            component="code"
            aria-label="Webhook signing secret"
            sx={{ wordBreak: "break-all", fontFamily: "monospace" }}
          >
            {revealedSecret}
          </Typography>
        </Alert>
      ) : null}

      {testResult !== null ? (
        <Alert
          severity={testResult.delivered ? "success" : "error"}
          onClose={() => setTestResult(null)}
          sx={{ mb: 3 }}
          role="status"
        >
          Test delivery {testResult.delivered ? "succeeded" : "failed"}
          {testResult.statusCode !== null ? ` (HTTP ${testResult.statusCode})` : ""} after{" "}
          {testResult.attempts} attempt{testResult.attempts === 1 ? "" : "s"}.
        </Alert>
      ) : null}

      {data.endpoints.length === 0 ? (
        <Typography color="text.secondary">No webhook endpoints configured.</Typography>
      ) : (
        <Box component="ul" aria-label="Webhook endpoints" sx={{ listStyle: "none", p: 0, m: 0 }}>
          {data.endpoints.map((endpoint) => (
            <Box
              component="li"
              key={endpoint.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                py: 1.5,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Typography sx={{ flexGrow: 1, wordBreak: "break-all" }}>{endpoint.url}</Typography>
              <Chip
                label={endpoint.status}
                size="small"
                color={endpoint.status === "active" ? "success" : "default"}
              />
              <Button
                size="small"
                onClick={() => handleTest(endpoint.id)}
                disabled={test.isPending}
              >
                Send test
              </Button>
              <Button
                size="small"
                color="error"
                onClick={() => del.mutate(endpoint.id)}
                disabled={del.isPending}
                aria-label={`Delete webhook ${endpoint.url}`}
              >
                Delete
              </Button>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
